import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { Printer } from "lucide-react";
import {
  prescriptionQueryKeys,
  prescriptionsApi,
} from "../../shared/api/prescriptions";
import { encounterQueryKeys, encountersApi } from "../../shared/api/encounters";
import { doctorsApi } from "../../shared/api/staff";
import {
  clinicFeaturesApi,
  clinicFeaturesQueryKeys,
} from "../../shared/api/clinicFeatures";
import {
  printTemplatesApi,
  printTemplatesQueryKeys,
} from "../../shared/api/printTemplates";
import { formatDate, formatVnd } from "../../shared/format";
import { Button } from "@/components/ui/button";

/**
 * Printable prescription (page 35). Browser print only — backend PDF storage + e-sign are Pro
 * (ADR-0015). A QR to the public no-login visit view (via the encounter's publicToken) is printed
 * regardless of whether the prescription has any medicines. Paper size, header title, signature
 * label, and footer come from the per-tenant
 * prescription template (T86 — `/settings/print-templates`); falls back to sensible defaults if
 * the template fetch fails.
 */
export function PrescriptionPrintPage() {
  const { id = "" } = useParams();
  const query = useQuery({
    queryKey: prescriptionQueryKeys.detail(id),
    queryFn: () => prescriptionsApi.get(id),
    enabled: !!id,
  });
  const tpl = useQuery({
    queryKey: printTemplatesQueryKeys.all(),
    queryFn: () => printTemplatesApi.get(),
  });
  // Show prices on the printed toa only when the clinic enabled it (ADR-0027).
  const features = useQuery({
    queryKey: clinicFeaturesQueryKeys.current(),
    queryFn: () => clinicFeaturesApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  const showPrice = features.data?.showPriceOnRx === true;
  // The prescription's encounter carries the public (no-login) view token; the
  // QR points there so the patient can see the full visit detail — shown even
  // when the prescription has no medicines.
  const encounterId = query.data?.encounterId ?? "";
  const enc = useQuery({
    queryKey: encounterQueryKeys.detail(encounterId),
    queryFn: () => encountersApi.get(encounterId),
    enabled: !!encounterId,
  });
  // Prescribing doctor's uploaded signature image (printed above the name).
  const doctorId = query.data?.doctorId ?? "";
  const sig = useQuery({
    queryKey: ["doctor-signature", doctorId],
    queryFn: () => doctorsApi.signatureUrl(doctorId),
    enabled: !!doctorId,
  });

  if (query.isLoading)
    return <p className="text-sm text-muted-foreground">Đang tải…</p>;
  if (query.isError || !query.data)
    return <p className="text-sm text-destructive">Không tìm thấy đơn thuốc.</p>;

  const rx = query.data;
  const t = tpl.data?.prescription;
  const paperSize = t?.paperSize ?? "A5";
  const headerTitle = t?.headerTitle?.trim() || "ĐƠN THUỐC";
  const signatureLabel = t?.signatureLabel?.trim() || "Bác sĩ";
  const footerText = t?.footerText?.trim() ?? "";
  const headerSubtitle = t?.headerSubtitle?.trim() ?? "";
  const sheetMaxWidth = paperSize === "A4" ? "210mm" : "148mm";
  const publicUrl = enc.data?.publicToken
    ? `${window.location.origin}/view-order/${enc.data.publicToken}/${enc.data.orderCode ?? ""}.html`
    : null;

  return (
    <>
      <style>{`
        @page { size: ${paperSize}; margin: 12mm; }
        @media print {
          body * { visibility: hidden; }
          .rx-print, .rx-print * { visibility: visible; }
          .rx-print { position: absolute; left: 0; top: 0; width: 100%; }
        }
        .rx-print { max-width: ${sheetMaxWidth}; margin: 0 auto; font-size: 13px; color:#111; line-height: 1.4; }
        .rx-print h2 { text-align:center; margin: 4px 0; letter-spacing: 0.04em; }
        .rx-print table { width:100%; border-collapse: collapse; margin-top: 8px; font-variant-numeric: tabular-nums; }
        .rx-print th, .rx-print td { border:1px solid #999; padding:4px 6px; text-align:left; font-size:12px; vertical-align: top; }
        .rx-print tr { break-inside: avoid; page-break-inside: avoid; }
        .rx-sign { margin-top: 28px; display:flex; justify-content:space-between; align-items:flex-end; gap: 12px; break-inside: avoid; page-break-inside: avoid; }
        .rx-sign .rx-signer { text-align:center; min-width: 200px; }
        .rx-qr { text-align:center; }
        .rx-qr div { font-size: 10px; color:#444; margin-top: 4px; max-width: 110px; }
        .rx-footer { margin-top: 16px; padding-top: 6px; border-top: 1px dashed #aaa; text-align:center; font-style:italic; font-size: 11px; break-inside: avoid; page-break-inside: avoid; }
        @media (max-width: 900px) {
          .rx-print { padding: 0 12px; }
        }
      `}</style>

      <div className="mb-3 print:hidden">
        <Button type="button" onClick={() => window.print()}>
          <Printer />
          In đơn thuốc
        </Button>
      </div>

      <div className="rx-print">
        <h2>{headerTitle}</h2>
        {headerSubtitle && (
          <div style={{ textAlign: "center", fontSize: 11 }}>
            {headerSubtitle}
          </div>
        )}
        <div>
          <strong>Bệnh nhân:</strong> {rx.patientName}
          {rx.patientCode ? ` (${rx.patientCode})` : ""}
          {rx.patientPhone ? ` — ĐT: ${rx.patientPhone}` : ""}
        </div>
        {/* Chẩn đoán bắt buộc trên đơn thuốc (Thông tư 04/2022/TT-BYT). Trước đây bản in
            không có, vì đơn thuốc chưa lưu chẩn đoán. */}
        {rx.icd10Code && (
          <div>
            <strong>Chẩn đoán:</strong> {rx.icd10LabelVi || rx.icd10Code}
            {rx.icd10LabelVi ? ` (${rx.icd10Code})` : ""}
          </div>
        )}
        <div>
          <strong>Bác sĩ kê đơn:</strong> {rx.doctorName}
        </div>
        <div>
          <strong>Ngày kê:</strong> {formatDate(rx.createdAt)}
          {rx.validityStart ? ` — Hiệu lực từ: ${formatDate(rx.validityStart)}` : ""}
        </div>

        <table>
          <thead>
            <tr>
              <th style={{ width: 24 }}>#</th>
              <th>Thuốc</th>
              <th>Liều — Tần suất — Đường dùng</th>
              <th style={{ width: 70, textAlign: "right" }}>SL</th>
              {showPrice && <th style={{ width: 80, textAlign: "right" }}>Đơn giá</th>}
              {showPrice && <th style={{ width: 90, textAlign: "right" }}>Thành tiền</th>}
            </tr>
          </thead>
          <tbody>
            {rx.items.map((it, i) => (
              <tr key={it.id}>
                <td>{i + 1}</td>
                <td>
                  {it.medicineName}
                  {it.medicineStrength ? ` · ${it.medicineStrength}` : ""}
                  {it.instructions ? (
                    <div style={{ fontStyle: "italic", fontSize: 11 }}>
                      {it.instructions}
                    </div>
                  ) : null}
                </td>
                <td>
                  {it.dosage} — {it.frequency} — {it.route}
                </td>
                <td style={{ textAlign: "right" }}>
                  {it.quantity} {it.unit}
                </td>
                {showPrice && (
                  <td style={{ textAlign: "right" }}>{formatVnd(it.unitPriceVnd ?? 0)}</td>
                )}
                {showPrice && (
                  <td style={{ textAlign: "right" }}>{formatVnd(it.lineTotalVnd ?? 0)}</td>
                )}
              </tr>
            ))}
            {showPrice && (
              <tr>
                <td colSpan={4} style={{ textAlign: "right", fontWeight: 700 }}>
                  Tổng tiền thuốc
                </td>
                <td colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>
                  {formatVnd(rx.items.reduce((s, it) => s + (it.lineTotalVnd ?? 0), 0))}₫
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="rx-sign">
          <div className="rx-qr">
            {publicUrl ? (
              <>
                <QRCodeSVG value={publicUrl} size={90} />
                <div>Quét mã để xem chi tiết lần khám</div>
              </>
            ) : null}
          </div>
          <div className="rx-signer">
            <div>Ngày …… tháng …… năm ……</div>
            <div style={{ marginTop: 4 }}>
              <strong>{signatureLabel}</strong>
            </div>
            {sig.data ? (
              <img
                src={sig.data}
                alt="Chữ ký bác sĩ"
                style={{ height: 52, margin: "6px auto 2px", objectFit: "contain" }}
              />
            ) : (
              <div style={{ marginTop: 48 }} />
            )}
            <div>{rx.doctorName}</div>
          </div>
        </div>
        {footerText && <div className="rx-footer">{footerText}</div>}
      </div>
    </>
  );
}
